import React, { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreateCharacter.module.css';
import { api } from '../../api';
import { imageIcon } from '../../assets/icons';
import Notification from './Notification';
import { Visibility } from '../../api/types/chat';

interface FormDataType {
  name: string;
  tagline: string;
  description: string;
  greeting: string;
  external_description: string;
  visibility: Visibility;
}

interface NotificationType {
  message: string;
  type: 'info' | 'success' | 'error';
}

function Create(): JSX.Element {
  const navigate = useNavigate();
  const [showMoreOptions, setShowMoreOptions] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataType>({
    name: '',
    tagline: '',
    description: '',
    greeting: '',
    external_description: '',
    visibility: 'public' // Default visibility is public
  });
  const [icon, setIcon] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [notification, setNotification] = useState<NotificationType | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files[0]) {
      setIcon(files[0]);
    }
  };

  const toggleMoreOptions = (): void => {
    setShowMoreOptions(prev => !prev);
  };

  // Direct handler for visibility changes
  const handleVisibilityChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const newVisibility = e.target.value as Visibility;
    setFormData(prev => ({
      ...prev,
      visibility: newVisibility
    }));
    
    // Show notification
    const visibilityLabels: Record<Visibility, string> = {
      'public': 'Public',
      'private': 'Private',
      'unlisted': 'Unlisted'
    };
    
    setNotification({
      message: `Character visibility set to ${visibilityLabels[newVisibility]}`,
      type: 'info'
    });
    
    console.log(`Visibility changed to: ${newVisibility}`);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Create the character
      const character = await api.characters.create({
        display_name: formData.name,
        tagline: formData.tagline,
        description: formData.description,
        greeting: formData.greeting,
        external_description: formData.external_description,
        icon: icon || undefined,
        visibility: formData.visibility
      });

      // Create a chat with the new character
      const chat = await api.chats.create(character.id);

      // Navigate to the new chat
      navigate(`/chats/${chat.id}`);
    } catch (err: any) {
      // Handle DRF validation errors properly
      const errorMessage = err.response?.data?.detail || 
                         Object.values(err.response?.data || {})[0]?.[0] ||
                         'Failed to create character';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.createContainer}>
      {notification && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
          onClose={() => setNotification(null)} 
        />
      )}
      
      <div className={styles.characterIconContainer}>
        <label htmlFor="icon-upload" className={styles.characterIcon}>
          {icon ? (
            <img 
              src={URL.createObjectURL(icon)} 
              alt="Character icon" 
              className={styles.previewIcon}
            />
          ) : (
            <span className={styles.editIcon}>
              <img src={imageIcon} alt="Upload" className={styles.uploadIcon} />
            </span>
          )}
          <input
            type="file"
            id="icon-upload"
            accept="image/*"
            onChange={handleIconChange}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      <form className={styles.createForm} onSubmit={handleSubmit}>
        {error && <div className={styles.error}>{error}</div>}
        
        <div className={styles.inputGroup}>
          <label htmlFor="name">Character name</label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="e.g. Albert Einstein"
            maxLength={20}
            value={formData.name}
            onChange={handleChange}
            required
          />
          <span className={styles.charCount}>{formData.name.length}/20</span>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="tagline">Tagline</label>
          <input
            type="text"
            id="tagline"
            name="tagline"
            placeholder="Add a short tagline of your Character"
            maxLength={50}
            value={formData.tagline}
            onChange={handleChange}
            required
          />
          <span className={styles.charCount}>{formData.tagline.length}/50</span>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            placeholder="How would your Character describe themselves?"
            maxLength={6250}
            value={formData.description}
            onChange={handleChange}
            required
          />
          <span className={styles.charCount}>{formData.description.length}/6250</span>
        </div>

        <button 
          type="button" 
          onClick={toggleMoreOptions} 
          className={styles.moreOptionsButton}
        >
          More options
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className={`${styles.chevronIcon} ${showMoreOptions ? styles.chevronRotated : ''}`}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>

        {showMoreOptions && (
          <div className={styles.inputGroup}>
            <label htmlFor="external_description">External Description <span className={styles.helperText}>only used in scenarios</span></label>
            <textarea
              id="external_description"
              name="external_description"
              placeholder="What do other people know about this Character?"
              maxLength={256}
              value={formData.external_description}
              onChange={handleChange}
              required
            />
            <span className={styles.charCount}>{formData.external_description.length}/256</span>
          </div>
        )}

        <div className={styles.inputGroup}>
          <label htmlFor="greeting">Greeting</label>
          <textarea
            id="greeting"
            name="greeting"
            placeholder="e.g. Hello, I am Albert. Ask me anything about my scientific contributions."
            maxLength={2048}
            value={formData.greeting}
            onChange={handleChange}
            required
          />
          <span className={styles.charCount}>{formData.greeting.length}/2048</span>
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="visibility">Visibility</label>
          <div className={styles.visibilitySelector}>
            <div className={styles.visibilityWrapper}>
              <span className={styles.visibilityIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </span>
              <select
                id="visibility"
                name="visibility"
                value={formData.visibility}
                onChange={handleVisibilityChange}
                className={styles.visibilitySelect}
              >
                <option value="public">Public</option>
                <option value="unlisted">Unlisted</option>
                <option value="private">Private</option>
              </select>
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          className={styles.createButton}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create Character'}
        </button>
      </form>
    </div>
  );
}

export default Create; 